// ─── CSV-utils — generieke parsers voor IC-imports (H2.3) ─────────────────
//
// Pure functies, geen Prisma, geen node:* imports. Werkt zowel browser-side
// (CSVColumnMapper-component) als server-side (POST /ic/tankpassen/:id/transactions/import).
//
// Ontwerp:
//   - parseCsv(text, opts): generieke split + per-rij parser, returnt
//     `Array<Record<string, string>>` met header-map gemapt naar canonical keys.
//   - parseCsvFuelTransactions(text, opts?): IC-tankpas-specifieke wrapper die
//     de canonical FuelCardTransactionRow oplevert + numerieke parsing per veld.
//
// Datum-formaten: DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD; eerste die parseert wint.
// Decimaal-formaten: NL "1.234,56" (punt = duizend, komma = decimaal) én
// EN "1,234.56" worden ondersteund. Heuristic: als komma rechts van laatste
// punt staat, is komma decimaal-separator.
//
// BOM (﻿) wordt gestripped van het eerste teken. Lege regels worden
// genegeerd. Onbekende kolommen worden bewaard onder hun originele header.

export interface CsvParseOptions {
  /** Mapping van bestand-header → canonical key. Niet-gemapte headers blijven hun originele naam. */
  headerMap?: Record<string, string>
  /** Forced delimiter; default: auto-detect uit eerste regel (";", "," of "\t"). */
  delimiter?: ';' | ',' | '\t'
}

export interface CsvParseResult {
  headers: string[]
  /** Originele headers zoals in bestand staan (voor mapping-UI). */
  rawHeaders: string[]
  rows: Array<Record<string, string>>
  delimiter: ';' | ',' | '\t'
}

/**
 * Strip BOM en split op nieuwe regel. Behoudt lege regels in tussen-positie
 * niet (filter ze direct).
 */
function splitLines(text: string): string[] {
  const stripped = text.startsWith('﻿') ? text.slice(1) : text
  return stripped
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
}

/**
 * Detecteer delimiter op basis van eerste regel. Pakt delimiter met meeste
 * voorkomens.
 */
function detectDelimiter(headerLine: string): ';' | ',' | '\t' {
  const counts = {
    ';': (headerLine.match(/;/g) ?? []).length,
    ',': (headerLine.match(/,/g) ?? []).length,
    '\t': (headerLine.match(/\t/g) ?? []).length,
  } as const
  if (counts['\t'] >= counts[';'] && counts['\t'] >= counts[',']) return '\t'
  if (counts[';'] >= counts[',']) return ';'
  return ','
}

/**
 * Parse één CSV-regel met respect voor double-quoted velden.
 * Quoted velden mogen de delimiter bevatten en escapen quotes als "".
 */
function parseLine(line: string, delimiter: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"'
          i += 1
        } else {
          inQuotes = false
        }
      } else {
        cur += ch
      }
    } else if (ch === '"' && cur === '') {
      inQuotes = true
    } else if (ch === delimiter) {
      out.push(cur)
      cur = ''
    } else {
      cur += ch
    }
  }
  out.push(cur)
  return out.map((s) => s.trim())
}

/**
 * Algemene CSV-parser. Returnt rows als records keyed op canonical header
 * (na headerMap-mapping). Throws op kapotte rij (verkeerd aantal kolommen).
 */
export function parseCsv(text: string, opts: CsvParseOptions = {}): CsvParseResult {
  const lines = splitLines(text)
  if (lines.length === 0) {
    return { headers: [], rawHeaders: [], rows: [], delimiter: ';' }
  }
  const headerLine = lines[0]!
  const delimiter = opts.delimiter ?? detectDelimiter(headerLine)
  const rawHeaders = parseLine(headerLine, delimiter)
  const headers = rawHeaders.map((h) => opts.headerMap?.[h] ?? opts.headerMap?.[h.toLowerCase()] ?? h)

  const rows: Array<Record<string, string>> = []
  for (let i = 1; i < lines.length; i += 1) {
    const line = lines[i]!
    const cols = parseLine(line, delimiter)
    if (cols.length !== headers.length) {
      throw new Error(`CSV-regel ${i + 1}: verwachtte ${headers.length} kolommen, kreeg ${cols.length}`)
    }
    const row: Record<string, string> = {}
    for (let j = 0; j < headers.length; j += 1) {
      row[headers[j]!] = cols[j] ?? ''
    }
    rows.push(row)
  }
  return { headers, rawHeaders, rows, delimiter }
}

// ─── Helpers voor numerieke + datum-parsing ─────────────────────────────────

/**
 * Parse "1.234,56" / "1,234.56" / "1234,56" / "1234.56" / "1.234" naar number.
 * Leeg → 0. Onparseerbaar → throw.
 */
export function parseDecimal(input: string): number {
  const s = input.trim().replace(/\s+/g, '')
  if (s === '') return 0
  // Detect of komma decimaal-separator is door te kijken waar laatste komma vs punt staat.
  const lastComma = s.lastIndexOf(',')
  const lastDot = s.lastIndexOf('.')
  let normalized = s
  if (lastComma === -1 && lastDot === -1) {
    normalized = s
  } else if (lastComma > lastDot) {
    // komma is decimaal-separator → punten zijn duizend-separators.
    normalized = s.replace(/\./g, '').replace(',', '.')
  } else {
    // punt is decimaal-separator → komma's zijn duizend-separators.
    normalized = s.replace(/,/g, '')
  }
  const n = Number(normalized)
  if (Number.isNaN(n)) throw new Error(`Onparseerbaar getal: "${input}"`)
  return n
}

/**
 * Parse datum naar ISO YYYY-MM-DD.
 * Ondersteunt DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD, YYYY/MM/DD.
 * Throw bij onparseerbaar.
 */
export function parseCsvDate(input: string): string {
  const s = input.trim()
  if (s === '') throw new Error('Lege datum')

  // YYYY-MM-DD of YYYY/MM/DD
  const ymd = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(s)
  if (ymd) {
    const y = ymd[1]!
    const m = ymd[2]!.padStart(2, '0')
    const d = ymd[3]!.padStart(2, '0')
    return `${y}-${m}-${d}`
  }
  // DD-MM-YYYY of DD/MM/YYYY
  const dmy = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(s)
  if (dmy) {
    const d = dmy[1]!.padStart(2, '0')
    const m = dmy[2]!.padStart(2, '0')
    const y = dmy[3]!
    return `${y}-${m}-${d}`
  }
  throw new Error(`Onbekend datumformaat: "${input}"`)
}

// ─── Fuel-card-specifieke parser ────────────────────────────────────────────

/**
 * Canonical-headers waar parseCsvFuelTransactions naar mapt:
 *   datum, liters, bedragExclBtw, btwBedrag, bedragInclBtw, locatie?
 */
export interface FuelCardTransactionRow {
  datum: string // ISO YYYY-MM-DD
  liters: number
  bedragExclBtw: number
  btwBedrag: number
  bedragInclBtw: number
  locatie?: string
}

/**
 * Default header-map die de meest-voorkomende kolomnamen bij Shell, BP en
 * Total tankpas-exports dekt. Caller kan eigen mapping meegeven.
 */
export const DEFAULT_FUEL_HEADER_MAP: Record<string, string> = {
  // datum
  datum: 'datum',
  date: 'datum',
  transactiedatum: 'datum',
  'transaction date': 'datum',
  // liters
  liters: 'liters',
  hoeveelheid: 'liters',
  quantity: 'liters',
  volume: 'liters',
  // bedrag excl btw
  'bedrag excl btw': 'bedragExclBtw',
  'bedrag excl': 'bedragExclBtw',
  'netto bedrag': 'bedragExclBtw',
  'amount excl vat': 'bedragExclBtw',
  // btw
  btw: 'btwBedrag',
  'btw bedrag': 'btwBedrag',
  vat: 'btwBedrag',
  'vat amount': 'btwBedrag',
  // bedrag incl btw
  'bedrag incl btw': 'bedragInclBtw',
  'bedrag incl': 'bedragInclBtw',
  'bruto bedrag': 'bedragInclBtw',
  totaal: 'bedragInclBtw',
  'amount incl vat': 'bedragInclBtw',
  total: 'bedragInclBtw',
  // locatie
  locatie: 'locatie',
  station: 'locatie',
  pomp: 'locatie',
  'site name': 'locatie',
}

/**
 * Parse CSV-tekst van een fuel-card-export naar `FuelCardTransactionRow[]`.
 * - Strippt BOM, detecteert delimiter, mapped headers naar canonical keys.
 * - Parseert NL-decimalen ("1.234,56") en US-decimalen.
 * - Parseert datums DD-MM-YYYY of YYYY-MM-DD.
 * - Throwt met regelnummer + reden bij kapotte rij.
 *
 * @param text — ruwe CSV-string
 * @param opts.headerMap — extra mapping bovenop DEFAULT_FUEL_HEADER_MAP
 */
export function parseCsvFuelTransactions(
  text: string,
  opts: { headerMap?: Record<string, string>; delimiter?: ';' | ',' | '\t' } = {},
): FuelCardTransactionRow[] {
  const merged: Record<string, string> = { ...DEFAULT_FUEL_HEADER_MAP }
  // case-insensitive defaults: voeg lowercase-keys toe.
  for (const k of Object.keys(DEFAULT_FUEL_HEADER_MAP)) {
    merged[k.toLowerCase()] = DEFAULT_FUEL_HEADER_MAP[k]!
  }
  if (opts.headerMap) {
    for (const [k, v] of Object.entries(opts.headerMap)) {
      merged[k] = v
      merged[k.toLowerCase()] = v
    }
  }

  const parsed = parseCsv(text, { headerMap: merged, delimiter: opts.delimiter })
  const required = ['datum', 'liters', 'bedragExclBtw', 'btwBedrag', 'bedragInclBtw']
  for (const r of required) {
    if (!parsed.headers.includes(r)) {
      throw new Error(`Verplichte kolom ontbreekt: ${r} (gevonden headers: ${parsed.rawHeaders.join(', ')})`)
    }
  }

  const out: FuelCardTransactionRow[] = []
  for (let i = 0; i < parsed.rows.length; i += 1) {
    const row = parsed.rows[i]!
    const lineNumber = i + 2 // header is regel 1
    try {
      const datum = parseCsvDate(row.datum ?? '')
      const liters = parseDecimal(row.liters ?? '')
      const bedragExclBtw = parseDecimal(row.bedragExclBtw ?? '')
      const btwBedrag = parseDecimal(row.btwBedrag ?? '')
      const bedragInclBtw = parseDecimal(row.bedragInclBtw ?? '')
      const locatie = (row.locatie ?? '').trim() || undefined
      out.push({ datum, liters, bedragExclBtw, btwBedrag, bedragInclBtw, locatie })
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'onbekende fout'
      throw new Error(`Regel ${lineNumber}: ${reason}`)
    }
  }
  return out
}
